import type { Brand } from '@/components/modules/brands-selector';

import type { DeploymentState } from '../../theme/tokens';
import type { DeploymentStatus, DeploymentStep, TechnicalDetails, TechnicalField } from '../../mock-data/types';
// Reuse the deploy-strip step-catalog humaniser so the verbose deployment list labels
// its real pipeline steps IDENTICALLY to the deploy strip + "Important Now" banner.
import { humanizeStep } from '../home/components/BrandDeploymentStrip/stepMeta';

/**
 * Derive the Casino-Settings "Technical details" view from a brand's real
 * `casino-instance/list` record. There is NO mock here; everything below is read
 * straight off the brand the top bar has selected:
 *
 *  - identity from `id`, `user_id`, `tenant_id`, `casino_data.details.{brand,domain}`
 *  - dates from `created_at`, `updated_at`, `casino_data.invoices.next_invoice_date`
 *  - deploy from `status` + `status_details` (the live content-deployment pipeline):
 *    the verbose step breakdown, progress, the pipeline + session ids, and a
 *    "Deployed" date DERIVED from the final completed step's timestamp (the record
 *    carries no dedicated deploy-finished field).
 *
 * Facts in this spectrum the API does not expose (hosting environment / region) are
 * simply omitted, never invented.
 */

/** `brand.status` while Lumio is still deploying the casino's content. */
const DEPLOYING_STATUS = 'content_deployment_in_progress';

const EMPTY_DEPLOYMENT: DeploymentStatus = {
  state: 'live',
  progress: 0,
  counts: { completed: 0, active: 0, pending: 0, failed: 0, total: 0 },
  steps: [],
};

/** ISO 8601 (`created_at` / `updated_at` / `next_invoice_date`) → "Jun 11, 2026"; '' if missing/invalid. */
const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return formatMs(ms);
};

/** Epoch ms → "Jun 11, 2026". */
const formatMs = (ms: number): string =>
  new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

/** Pipeline step timestamp (unix SECONDS) → "Jun 11, 08:41". */
const formatUnix = (sec: number): string =>
  new Date(sec * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Bare host (e.g. "cajafuerte.casino") → absolute https URL for the "Visit" link. */
const toHref = (domain: string): string => (/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);

/**
 * The real "Deployed" date: the moment the LAST build step completed. A live brand
 * reads the finish date; a brand still deploying reads "In progress" (not final yet).
 */
const deployedLabel = (brand: Brand): string => {
  if (brand.status === DEPLOYING_STATUS) return 'In progress';
  const completed = brand.status_details?.steps?.completed;
  const stamps = completed ? Object.values(completed) : [];
  if (stamps.length === 0) return '';
  return formatMs(Math.max(...stamps) * 1000);
};

/** Build the verbose deployment status from the brand's real `status` + pipeline steps. */
const buildDeployment = (brand: Brand): DeploymentStatus => {
  const deploying = brand.status === DEPLOYING_STATUS;
  const steps = brand.status_details?.steps;
  if (!steps) return { ...EMPTY_DEPLOYMENT, state: deploying ? 'deploying' : 'live' };

  const completedEntries = Object.entries(steps.completed); // [name, unixSeconds]
  const failedEntries = Object.entries(steps.failed); // [name, { failed_at }]
  const skippedKeys = Object.keys(steps.skipped);

  const completed = completedEntries.length;
  const failed = failedEntries.length;
  const active = steps.active.length;
  const pending = steps.pending.length;
  const skipped = skippedKeys.length;
  const total = completed + failed + active + pending + skipped;

  // A failed step always reads as "needs attention", even when the brand's top-level
  // status has already moved off 'content_deployment_in_progress' (a pipeline can
  // finish or clear while leaving failed entries behind). Otherwise: deploying vs live.
  const state: DeploymentState = failed > 0 ? 'attention' : deploying ? 'deploying' : 'live';

  // Order the list so problems + current work read first and the (chronological)
  // completed history reads last: failed → active → pending → completed → skipped.
  const list: DeploymentStep[] = [
    ...failedEntries.map(([name, info]) => ({ label: humanizeStep(name), state: 'failed' as const, at: formatUnix(info.failed_at) })),
    ...steps.active.map((name) => ({ label: humanizeStep(name), state: 'active' as const })),
    ...steps.pending.map((name) => ({ label: humanizeStep(name), state: 'pending' as const })),
    ...completedEntries
      .sort((a, b) => a[1] - b[1])
      .map(([name, ts]) => ({ label: humanizeStep(name), state: 'completed' as const, at: formatUnix(ts) })),
    ...skippedKeys.map((name) => ({ label: humanizeStep(name), state: 'skipped' as const })),
  ];

  return {
    state,
    // Skipped steps are resolved (intentionally not run), so they count toward
    // completion; otherwise a finished pipeline with skips never reaches 100%.
    progress: total > 0 ? Math.round(((completed + skipped) / total) * 100) : 0,
    counts: { completed, active, pending, failed, total },
    steps: list,
  };
};

/** Resolve the whole Technical-details view for the selected brand (empty when none). */
export const resolveTechnicalDetails = (brand: Brand | undefined): TechnicalDetails => {
  if (!brand) return { deployment: { ...EMPTY_DEPLOYMENT }, fields: [] };

  const details = brand.casino_data?.details;
  const name = details?.brand?.trim() ?? '';
  const domain = details?.domain?.trim() ?? '';
  const nextInvoice = formatDate(brand.casino_data?.invoices?.next_invoice_date);
  const pipelineId = brand.status_details?.pipeline_id;
  const sessionId = brand.deployments?.[0]?.session_id;
  const deployed = deployedLabel(brand);

  const fields: TechnicalField[] = [];
  const add = (field: TechnicalField | null): void => {
    if (field) fields.push(field);
  };

  add(name ? { id: 'name', label: 'Casino name', value: name, icon: 'globe' } : null);
  add({ id: 'casino-id', label: 'Casino ID', value: brand.id, mono: true, copyable: true });
  add(domain ? { id: 'domain', label: 'Domain', value: domain, mono: true, copyable: true, href: toHref(domain) } : null);
  add(brand.tenant_id ? { id: 'tenant-id', label: 'Tenant ID', value: brand.tenant_id, mono: true, copyable: true } : null);
  add(brand.user_id ? { id: 'owner-id', label: 'Owner ID', value: brand.user_id, mono: true, copyable: true } : null);
  add(brand.created_at ? { id: 'created', label: 'Created', value: formatDate(brand.created_at), icon: 'calendar' } : null);
  add(deployed ? { id: 'deployed', label: 'Deployed', value: deployed, icon: 'calendar' } : null);
  add(brand.updated_at ? { id: 'updated', label: 'Last updated', value: formatDate(brand.updated_at), icon: 'clock' } : null);
  add(nextInvoice ? { id: 'next-invoice', label: 'Next invoice', value: nextInvoice, icon: 'wallet' } : null);
  add(pipelineId ? { id: 'pipeline-id', label: 'Pipeline ID', value: pipelineId, mono: true, copyable: true } : null);
  add(sessionId ? { id: 'session-id', label: 'Deployment session', value: sessionId, mono: true, copyable: true } : null);

  return { deployment: buildDeployment(brand), fields };
};

export default resolveTechnicalDetails;
